import ReactTooltip from '@components/DataDisplay/LegacyTooltip/LegacyTooltip'
import { InfoIconGray, InfoIconOutline } from '@icons'
import cn from '@integrations/classnames'
import type { ReactNode } from '@integrations/react'
import { useId } from '@integrations/react'

import styles from './InfoTooltip.module.scss'

interface Props {
  content: ReactNode
  label: string
  /** Name of the trigger button for assistive tech; defaults to `label` (the tooltip heading). */
  triggerLabel?: string
  /** `outline` is a thin glyph that takes the surrounding text colour (KPI labels); `solid` is the dark filled one. */
  variant?: 'solid' | 'outline'
  className?: string
  'data-test'?: string
}

function InfoTooltip({ content, label, triggerLabel, variant = 'solid', className, 'data-test': dataTest }: Props) {
  const tooltipId = `info-tooltip-${useId()}`
  const outline = variant === 'outline'

  return (
    <>
      <button
        type='button'
        className={cn(
          styles['trigger'],
          'info-tooltip-trigger',
          { [styles['trigger--outline']]: outline, 'info-tooltip-trigger--outline': outline },
          className
        )}
        data-tip
        data-for={tooltipId}
        data-test={dataTest}
        aria-label={triggerLabel ?? label}
      >
        {outline ? <InfoIconOutline /> : <InfoIconGray />}
      </button>
      <ReactTooltip
        id={tooltipId}
        place='top'
        type='dark'
        effect='solid'
        getContent={() => (
          <div className={cn(styles['tooltip-content'], 'info-tooltip-content')}>
            <b className={cn(styles['tooltip-title'], 'info-tooltip-title')}>{label}</b>
            {content}
          </div>
        )}
      />
    </>
  )
}

export default InfoTooltip
